import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
  type ReactNode,
} from 'react';

/**
 * The page-chrome channel: how a nested page tells the shell what to put in the header.
 *
 * ── WHY A CHANNEL RATHER THAN A PAGE HEADER ──────────────────────────────────────────
 *
 * The shell owns the header because the header must survive navigation — it is the part
 * of the application that does not unmount when the Outlet changes. But only the page
 * knows what it is showing, and only after it has loaded: the breadcrumb for
 * /admin/events/:id says the event's TITLE, which the shell would otherwise have to fetch
 * a second time purely to label a link. §8 forbids that duplicate fetch, so the page,
 * which already has the event, hands the label up.
 *
 * Nothing here reaches into the sidebar or the header DOM. A page publishes values; the
 * shell renders them.
 *
 * ── SAFE OUTSIDE THE SHELL ───────────────────────────────────────────────────────────
 *
 * usePageChrome is a NO-OP when there is no provider. This matters: EventWizard and
 * EventManage are shared with the Organizer portal, which has no chrome channel. They can
 * declare their breadcrumb unconditionally and it is simply ignored when an organizer is
 * the one looking at them — no `if (isAdmin)` inside a shared component.
 */

export interface Crumb {
  label: string;
  /** Omit for the current page — the last crumb is text, not a link. */
  to?: string;
}

export interface PageChrome {
  /**
   * The shell's own <h1>. OMIT IT when the page already renders its own heading, which
   * every screen that predates the shell does — otherwise the same words appear twice,
   * which is the stutter §32 is about.
   */
  title?: string;
  subtitle?: string;
  breadcrumb?: Crumb[];
  actions?: ReactNode;
}

interface ChromeChannel {
  chrome: PageChrome;
  publish: (chrome: PageChrome) => void;
  clear: () => void;
}

const NOOP: ChromeChannel = { chrome: {}, publish: () => {}, clear: () => {} };
const PageChromeContext = createContext<ChromeChannel>(NOOP);

export function PageChromeProvider({ children }: { children: ReactNode }) {
  const [chrome, setChrome] = useState<PageChrome>({});

  const publish = useCallback((next: PageChrome) => setChrome(next), []);
  const clear = useCallback(() => setChrome({}), []);

  const value = useMemo(() => ({ chrome, publish, clear }), [chrome, publish, clear]);
  return <PageChromeContext.Provider value={value}>{children}</PageChromeContext.Provider>;
}

/** What the shell reads. */
export function usePageChromeValue(): PageChrome {
  return useContext(PageChromeContext).chrome;
}

/**
 * Declare this page's chrome.
 *
 * Re-publishes when the SERIALIZABLE parts change. `actions` is a ReactNode and cannot be
 * compared, so it is read from a ref at publish time: pass `deps` when the actions need
 * to force a republish on their own. This is the same contract as any effect — it is
 * explicit rather than clever, because a chrome hook that republished on every render
 * would loop through the provider's state.
 */
export function usePageChrome(chrome: PageChrome, deps: readonly unknown[] = []): void {
  const { publish, clear } = useContext(PageChromeContext);

  const latest = useRef(chrome);
  latest.current = chrome;

  const crumbKey = JSON.stringify(chrome.breadcrumb ?? null);

  useEffect(() => {
    publish(latest.current);
    return clear;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publish, clear, chrome.title, chrome.subtitle, crumbKey, ...deps]);
}
